import React, { useEffect, useState } from "react";
import api from "../api";
import { hasPermission } from "../utils/auth";

export default function ContractTypesPage({ user }) {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const canRead = hasPermission(user, "contract-types:read");
  const canManage = hasPermission(user, "contract-types:manage");

  const fetchTypes = async () => {
    if (!canRead) {
      setMessage("You do not have permission to view contract types.");
      return;
    }
    try {
      const res = await api.get("/contract-types");
      setTypes(res.data);
    } catch (err) {
      console.error("Error fetching contract types:", err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, [canRead]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setDescription("");
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!name.trim()) return;
    if (!canManage) {
      setMessage("You do not have permission to manage contract types.");
      return;
    }

    try {
      if (editingId) {
        await api.put(`/contract-types/${editingId}`, {
          name,
          description,
          is_active: 1,
        });
        setMessage("Contract type updated.");
      } else {
        await api.post("/contract-types", { name, description });
        setMessage("Contract type created.");
      }

      resetForm();
      fetchTypes();
    } catch (err) {
      console.error("Error saving contract type:", err);
      setMessage("Failed to save contract type.");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setName(t.name);
    setDescription(t.description || "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contract type?")) return;
    if (!canManage) {
      setMessage("You do not have permission to manage contract types.");
      return;
    }
    try {
      await api.delete(`/contract-types/${id}`);
      setMessage("Contract type deleted.");
      fetchTypes();
    } catch (err) {
      console.error("Error deleting contract type:", err);
      setMessage("Failed to delete contract type.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-navy">Contract Types</h1>
        <p className="text-sm text-slate-600 mt-1">
          Manage the categories your contracts belong to (e.g. “Utility Bills”,
          “Rent”, “Employment”, “Service Agreement”).
        </p>
      </div>

      {message && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-2 text-sm text-amber-900">
          {message}
        </div>
      )}

      {!canRead && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3">
          You do not have permission to view contract types.
        </div>
      )}

      {/* Form + Table layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,2fr] gap-6">
        {/* Form card */}
        <form
          onSubmit={handleCreateOrUpdate}
          className="bg-white shadow-sm rounded-xl border border-slate-200 p-5 space-y-4"
        >
          <h2 className="text-lg font-semibold text-navy mb-1">
            {editingId ? "Edit Contract Type" : "Create Contract Type"}
          </h2>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Utility Bills, Rent, Employment"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/70 focus:border-gold/70"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: short description of this contract type"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={!canManage}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold shadow-sm hover:bg-navy/90"
            >
              {editingId ? "Update Type" : "Create Type"}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs text-slate-600 hover:text-slate-900"
              >
                Cancel editing
              </button>
            )}
          </div>
        </form>

        {/* Table card */}
        <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">
              Existing Contract Types
            </h2>
            <span className="text-xs text-slate-500">
              {types.length} type{types.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-navy text-white text-left">
                  <th className="px-4 py-2 font-semibold">ID</th>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Description</th>
                  <th className="px-4 py-2 font-semibold">Active</th>
                  <th className="px-4 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t, idx) => (
                  <tr
                    key={t.id}
                    className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                  >
                    <td className="px-4 py-2 border-t border-slate-100">
                      {t.id}
                    </td>
                    <td className="px-4 py-2 border-t border-slate-100">
                      <span className="font-medium text-slate-900">
                        {t.name}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-t border-slate-100">
                      <span className="text-xs text-slate-700">
                        {t.description || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-t border-slate-100">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          t.is_active
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-slate-50 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {t.is_active ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-t border-slate-100">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          disabled={!canManage}
                          className="px-3 py-1.5 rounded-lg border border-navy text-navy text-xs font-semibold hover:bg-navy hover:text-white"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(t.id)}
                          disabled={!canManage}
                          className="px-3 py-1.5 rounded-lg border border-red-600 text-red-700 text-xs font-semibold hover:bg-red-600 hover:text-white"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {types.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-6 text-center text-sm text-slate-500"
                    >
                      No contract types defined yet. Create your first type on
                      the left.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
